import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Stack, styled } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { CustomButton } from "@/components/custom/custom-button";
import CustomPagination from "@/components/custom/custom-pagination";
import { DataTable } from "@/components/custom/data-table";
import { showToast } from "@/redux/actions/toast-actions";
import { useMessageTemplates, useToggleMessageTemplate } from "@/hooks/use-notifications";
import { getTemplateColumns } from "./_components/template-columns";
import TemplateFormDialog from "./_components/template-form-dialog";

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const MessageTemplates = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [dialog, setDialog] = useState({ open: false, template: null });
  const [togglingId, setTogglingId] = useState(null);

  const params = {
    page: Number(searchParams.get("page") || 0),
    size: Number(searchParams.get("size") || 10),
  };

  const { data, isLoading, isError } = useMessageTemplates(params);
  const toggleMutation = useToggleMessageTemplate();

  const handleToggle = (row) => {
    setTogglingId(row.id);
    toggleMutation.mutate(row.id, {
      onSuccess: (res) =>
        dispatch(showToast(
          res?.message ?? `Template ${row.active ? "disabled" : "enabled"}`,
          "success",
        )),
      onError: (err) =>
        dispatch(showToast(err?.message ?? "Failed to toggle template", "error")),
      onSettled: () => setTogglingId(null),
    });
  };

  const columns = useMemo(
    () =>
      getTemplateColumns({
        onEdit: (template) => setDialog({ open: true, template }),
        onToggle: handleToggle,
        togglingId,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingId],
  );

  return (
    <Card>
      <Stack direction="row" justifyContent="flex-end">
        <CustomButton
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setDialog({ open: true, template: null })}
          sx={{ height: 35 }}
        >
          New Template
        </CustomButton>
      </Stack>

      <DataTable
        data={data?.data ?? []}
        columns={columns}
        isLoading={isLoading}
        isError={isError}
        noDataMessage="No message templates found."
      />

      <CustomPagination total={data?.totalCount ?? 0} perPageOptions={[10, 25, 50, 100]} />

      <TemplateFormDialog
        open={dialog.open}
        template={dialog.template}
        onClose={() => setDialog({ open: false, template: null })}
      />
    </Card>
  );
};

export default MessageTemplates;